import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { HeaderContainer } from '../dashboard/AgentInfoHeader';
import LayoutWithSidebar from '../common/layoutWithSidebar';

const FlowDetailSkeleton: React.FC = () => {
  return (
    <LayoutWithSidebar margin={0}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header Skeleton */}
        <HeaderContainer
          sx={{
            borderRadius: 0,
            border: 'none',
            borderBottom: '1px solid #DCE0E7',
            margin: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Back Button Skeleton */}
            <Skeleton
              variant="circular"
              width={24}
              height={24}
              sx={{ minWidth: 24 }}
            />

            {/* Title and Description Skeleton */}
            <Box>
              <Skeleton
                variant="text"
                width={240}
                height={32}
                sx={{
                  fontSize: '24px',
                  mb: 0.5,
                }}
              />
              <Skeleton
                variant="text"
                width={320}
                height={20}
                sx={{
                  fontSize: '14px',
                }}
              />
            </Box>
          </Box>
        </HeaderContainer>

        {/* Main Content Area */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Main Flow Area Skeleton */}
          <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            {/* Flow Canvas Background */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: '#f8f9fa',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Flow Nodes Skeleton */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {/* First row of nodes */}
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Skeleton
                    variant="rectangular"
                    width={180}
                    height={80}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width={180}
                    height={80}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                </Box>

                {/* Second row of nodes */}
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Skeleton
                    variant="rectangular"
                    width={180}
                    height={80}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width={180}
                    height={80}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width={180}
                    height={80}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                </Box>
              </Box>

              {/* Background Grid Pattern */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `
                    linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px)
                  `,
                  backgroundSize: '20px 20px',
                  opacity: 0.5,
                  zIndex: -1,
                }}
              />
            </Box>

            {/* Flow Controls Skeleton - Bottom Left */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                zIndex: 1000,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  p: 1,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width={32}
                  height={32}
                  sx={{ borderRadius: '4px' }}
                />
                <Skeleton
                  variant="rectangular"
                  width={32}
                  height={32}
                  sx={{ borderRadius: '4px' }}
                />
                <Skeleton
                  variant="rectangular"
                  width={32}
                  height={32}
                  sx={{ borderRadius: '4px' }}
                />
                <Skeleton
                  variant="rectangular"
                  width={32}
                  height={32}
                  sx={{ borderRadius: '4px' }}
                />
              </Box>
            </Box>

            {/* Mini Map Skeleton - Bottom Right */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                zIndex: 1000,
              }}
            >
              <Skeleton
                variant="rectangular"
                width={120}
                height={80}
                sx={{
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                }}
              />
            </Box>
          </Box>

          {/* Execution History Sidebar Skeleton */}
          <Box
            sx={{
              width: 320,
              borderLeft: '1px solid #DCE0E7',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Execution History Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #DCE0E7',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 18,
                  color: '#2B2D3F',
                  letterSpacing: '-0.2px',
                }}
              >
                Execution History
              </Typography>
            </Box>

            {/* Execution History Content Skeleton */}
            <Box sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Search/Filter Skeleton */}
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={40}
                  sx={{ borderRadius: '8px' }}
                />

                {/* Execution Items List */}
                {[1, 2, 3, 4, 5, 6].map(item => (
                  <Box
                    key={item}
                    sx={{
                      p: 2,
                      border: '1px solid #DCE0E7',
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    {/* Status and Time */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Skeleton variant="text" width={60} height={20} />
                      <Skeleton variant="text" width={80} height={16} />
                    </Box>

                    {/* Job ID */}
                    <Skeleton
                      variant="text"
                      width="100%"
                      height={16}
                      sx={{ mb: 1 }}
                    />

                    {/* Duration */}
                    <Skeleton variant="text" width={100} height={16} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </LayoutWithSidebar>
  );
};

export default FlowDetailSkeleton;
